/**
 * Component for rendering vertical lane lines (solid and dashed) next to messages.
 * Handles thread continuity visualization.
 */

import React from "react";
import { Box } from "@mui/material";
import type { TimelineEvent, LaneThread } from "../types";

interface LaneSegmentsProps {
  events: TimelineEvent[];
  messageLanePositions: Map<number, { top: number; height: number }>;
  getLaneInfo: (threadId?: string) => LaneThread | undefined;
  threadMessageIndices: Map<string, { first: number; last: number }>;
  stickyLaneThreads: Set<string>;
  laneThickness: number;
  laneSpacing: number;
  lanesLeftOffset: number;
  dashedLineLength: number;
  otherThreadsLaneColor: string;
  laneThreadsCount: number;
  dateColumnWidth: number;
}

/**
 * Renders all lane segments for the timeline.
 * Each message gets a vertical lane segment with appropriate styling based on thread continuity.
 */
export function LaneSegments({
  events,
  messageLanePositions,
  getLaneInfo,
  threadMessageIndices,
  stickyLaneThreads,
  laneThickness,
  laneSpacing,
  lanesLeftOffset,
  dashedLineLength,
  otherThreadsLaneColor,
  laneThreadsCount,
  dateColumnWidth,
}: LaneSegmentsProps) {
  // Offset all lane positions by dateColumnWidth to account for left date column
  const effectiveLanesLeftOffset = lanesLeftOffset + dateColumnWidth;
  return (
    <>
      {events.map((event, index) => {
        const laneInfo = getLaneInfo(event.threadId);
        const positions = messageLanePositions.get(index);
        if (!positions) return null;

        // Determine lane position and color
        let laneX: number;
        let laneColor: string;

        if (laneInfo) {
          laneX =
            effectiveLanesLeftOffset +
            laneInfo.laneIndex * (laneThickness + laneSpacing);
          laneColor = laneInfo.color;
        } else {
          laneX =
            effectiveLanesLeftOffset +
            laneThreadsCount * (laneThickness + laneSpacing);
          laneColor = otherThreadsLaneColor;
        }

        // Determine thread continuity
        const threadIndices = event.threadId
          ? threadMessageIndices.get(event.threadId)
          : undefined;
        const isFirstMessageOfThread = threadIndices?.first === index;
        const isLastMessageOfThread = threadIndices?.last === index;
        const isThreadSticky = event.threadId
          ? stickyLaneThreads.has(event.threadId)
          : false;

        // Check if interrupted by another thread
        const prevEvent = index > 0 ? events[index - 1] : null;
        const nextEvent = index < events.length - 1 ? events[index + 1] : null;
        const isInterruptedAbove =
          prevEvent && prevEvent.threadId !== event.threadId;
        const isInterruptedBelow =
          nextEvent && nextEvent.threadId !== event.threadId;

        // Determine what to show above - subject shows when thread changes from previous
        const showsSubjectLine =
          !prevEvent || prevEvent.threadId !== event.threadId;
        const subjectInDOM = laneInfo && isFirstMessageOfThread;
        const hasSubjectVisible = subjectInDOM && !isThreadSticky;
        const showDashedAbove =
          laneInfo &&
          ((isFirstMessageOfThread && isThreadSticky) ||
            (!isFirstMessageOfThread && isInterruptedAbove));
        const showSolidAbove = hasSubjectVisible && prevEvent;

        // Determine what to show below
        const showDashedBelow =
          laneInfo && !isLastMessageOfThread && isInterruptedBelow;

        // Show solid connection above when same thread continues
        const showSolidConnectionAbove =
          laneInfo &&
          !isFirstMessageOfThread &&
          !isInterruptedAbove &&
          prevEvent?.threadId === event.threadId;

        const subjectDotGap = 4;
        // When there's a subject row (dot + text), the lane line should start below it
        // Use showsSubjectLine (thread changed) instead of subjectInDOM (which requires laneInfo)
        const subjectOffset = showsSubjectLine ? 17 : 0;
        const solidLineAboveHeight = hasSubjectVisible ? 24 : 0;

        // Calculate actual gap between messages
        const prevPositions =
          index > 0 ? messageLanePositions.get(index - 1) : null;
        const actualGap = prevPositions
          ? positions.top - (prevPositions.top + prevPositions.height)
          : 16;
        const connectionGap = Math.max(actualGap, 4);

        // Check if next message is same thread
        const showSolidConnectionBelow =
          laneInfo &&
          !isLastMessageOfThread &&
          !isInterruptedBelow &&
          nextEvent?.threadId === event.threadId;

        return (
          <React.Fragment key={`lane-seg-${index}`}>
            {/* Dashed line above (when subject is sticky or thread interrupted) */}
            {showDashedAbove && (
              <Box
                className="lane-segment-dashed-above"
                sx={{
                  position: "absolute",
                  left: laneX,
                  top: positions.top - dashedLineLength,
                  height: dashedLineLength,
                  width: laneThickness,
                  backgroundImage: `repeating-linear-gradient(to bottom, ${laneColor} 0px, ${laneColor} 4px, transparent 4px, transparent 8px)`,
                  zIndex: 2,
                  pointerEvents: "none",
                }}
              />
            )}

            {/* Solid line above to subject dot (when subject visible) */}
            {showSolidAbove && (
              <Box
                className="lane-segment-solid-above"
                sx={{
                  position: "absolute",
                  left: laneX,
                  top: positions.top - solidLineAboveHeight,
                  height: solidLineAboveHeight - subjectDotGap,
                  width: laneThickness,
                  backgroundColor: laneColor,
                  borderRadius: "4px 4px 0 0",
                  zIndex: 2,
                  pointerEvents: "none",
                }}
              />
            )}

            {/* Solid connection above (when previous message is from same thread) */}
            {showSolidConnectionAbove && (
              <Box
                className="lane-segment-connection-above"
                sx={{
                  position: "absolute",
                  left: laneX,
                  top: positions.top - connectionGap,
                  height: connectionGap,
                  width: laneThickness,
                  backgroundColor: laneColor,
                  zIndex: 2,
                  pointerEvents: "none",
                }}
              />
            )}

            {/* Main solid segment at message - starts after dot diameter + space */}
            <Box
              className="lane-segment-main"
              sx={{
                position: "absolute",
                left: laneX,
                top: positions.top + subjectOffset,
                height: positions.height - subjectOffset,
                width: laneThickness,
                backgroundColor: laneColor,
                borderRadius: `${showSolidConnectionAbove ? 0 : 4}px ${showSolidConnectionAbove ? 0 : 4}px ${showSolidConnectionBelow ? 0 : 4}px ${showSolidConnectionBelow ? 0 : 4}px`,
                zIndex: 2,
                pointerEvents: "none",
              }}
            />

            {/* Dashed line below (when thread continues but interrupted) */}
            {showDashedBelow && (
              <Box
                className="lane-segment-dashed-below"
                sx={{
                  position: "absolute",
                  left: laneX,
                  top: positions.top + positions.height,
                  height: dashedLineLength,
                  width: laneThickness,
                  backgroundImage: `repeating-linear-gradient(to bottom, ${laneColor} 0px, ${laneColor} 4px, transparent 4px, transparent 8px)`,
                  zIndex: 2,
                  pointerEvents: "none",
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}
